import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Home, Layers, Gauge,
  Sparkles, Wallet, Library, Lightbulb, Users, DollarSign,
  TrendingUp, Wrench, Target, Receipt, Link2, PenLine,
  Building2, Image as ImageIcon, Share2, HelpCircle, Play,
} from "lucide-react";
import DisclaimerBar from "@/components/DisclaimerBar";
import DemoDialog from "@/components/DemoDialog";

const STEPS = [
  {
    n: "01",
    icon: Target,
    title: "Pick your goal",
    body: "Every project starts with one of three goals — Flip for profit, Renovate to sell, or Renovate to live. Your choice shapes how results are framed, not the underlying numbers.",
  },
  {
    n: "02",
    icon: Home,
    title: "Create a project",
    body: "Name the property, add the address, and pick a currency (USD, GBP, EUR, CAD, AUD, NZD or ZAR). A project is the container for everything else.",
  },
  {
    n: "03",
    icon: DollarSign,
    title: "Enter the numbers",
    body: "On the Overview tab, fill in Purchase Price, Target Sale Price, Floor Area and Renovation Type. The dashboard's summary cards recompute live in your project currency.",
  },
  {
    n: "04",
    icon: Library,
    title: "Calibrate the Cost Library",
    body: "Open the Library tab inside your project and adjust any rates that don't match your market. Each project carries its own library so edits never bleed across deals. Rows show e.g. ZAR / sqm so the unit is unambiguous, an Undo button instantly reverts to the regional default, and one-click regional presets seed a starting point for seven property markets. New rooms or extensions you add later automatically pick up your saved rates.",
  },
  {
    n: "05",
    icon: Layers,
    title: "Model the renovation",
    body: "Move through Rooms, Exterior and Systems. Each tab auto-seeds typical upgrades. Tune scope, finish level and quantities — cost updates instantly, in your project currency.",
  },
  {
    n: "06",
    icon: Building2,
    title: "Add extensions or new builds",
    body: "Adding sqm? The Extensions tab lets you model single-storey, double-storey, loft, garage conversions, or knock-throughs. Each extension auto-seeds shell costs (foundation, frame, roof, services tie-in, council fees) and lets you nest the fitout rooms inside it. Cost-per-sqm switches to post-build GLA so the headline number reflects what you're actually selling.",
  },
  {
    n: "07",
    icon: Wallet,
    title: "Tune soft costs",
    body: "Agent fees, holding, contingency, transfer duty, permits. Each line can be a fixed amount or a percentage of purchase / sale / renovation — whichever matches how you plan it.",
  },
  {
    n: "08",
    icon: Gauge,
    title: "Decide with confidence",
    body: "Grade the project by the metrics that match your goal — profit and ROI for flippers, resale uplift for sellers, total spend and cost-per-room for homeowners. Benchmark against comparable projects in your market.",
  },
  {
    n: "10",
    icon: Receipt,
    title: "Track spend as you go",
    body: "Flip the header to Actual and the dashboard becomes a tracker. Log receipts against Rooms, Exterior, Systems or Soft Costs — optionally tagged to a specific room or line item. Flip back to Estimate any time; your plan and comparisons stay intact.",
  },
  {
    n: "11",
    icon: ImageIcon,
    title: "Drop in property photos",
    body: "On the Overview tab, click the photo dropzone or drag images straight from your desktop. Files are auto-resized to 1600px and compressed for fast loading — no URL hunting, no separate file host.",
  },
  {
    n: "12",
    icon: Share2,
    title: "Share with collaborators",
    body: "Hit the Share button in the dashboard header to invite teammates by email. Choose View only (read-only) or Can edit (full read + write, except deletion). Invites expire in 7 days and can be revoked instantly. Resend the invite link manually any time from the same dialog.",
  },
];

const GOALS = [
  {
    icon: TrendingUp,
    title: "Flip for profit",
    body: "Estimate renovation costs and calculate profit and ROI. Dashboard emphasises Est. Profit, ROI and Margin.",
  },
  {
    icon: Home,
    title: "Renovate to sell",
    body: "Understand renovation costs and maximise resale value. Dashboard emphasises Total Renovation Cost and Estimated Value Added.",
  },
  {
    icon: Wrench,
    title: "Renovate to live",
    body: "Plan your renovation costs and spend wisely. Dashboard emphasises Total Renovation Cost, Cost per Room and the budget breakdown.",
  },
];

const TIPS = [
  {
    icon: Library,
    title: "Start with a regional preset",
    body: "Library tab → pick your market (USA, UK, EU, AUS, NZ, ZA, CAN). Built-in rates are tuned per region — refine against your own quotes from there.",
  },
  {
    icon: Share2,
    title: "Share before the build kicks off",
    body: "Got a partner, a contractor or an agent who wants visibility? Use the Share button in the project header — View only is perfect for agents, Can edit lets a partner update line items as quotes come back.",
  },
  {
    icon: Lightbulb,
    title: "Scope matters more than rate",
    body: "For Exterior & Systems, toggle 'Repair' vs 'Replace' vs 'Upgrade' — this applies 0.4× / 1.0× / 1.4× multipliers. A cheaper roof repair can save a project.",
  },
  {
    icon: Building2,
    title: "Fitout rooms inside an extension",
    body: "Each Extension has its own Rooms list nested inside. Drop a kitchen + bathroom into a single-storey rear extension and they share the same library, currency and finish multipliers as your main rooms — no double-entry.",
  },
  {
    icon: Users,
    title: "Read the comparables panel",
    body: "On Overview, the Comparable Deals card shows your numbers against peers in your currency and size. Green = beating the market, red = below.",
  },
  {
    icon: Layers,
    title: "Bathroom tiling uses a 2.5× multiplier",
    body: "If your 25 sqm bathroom shows 62.5 sqm of tiling, that's floor + walls combined — standard for full-height tiled bathrooms.",
  },
  {
    icon: PenLine,
    title: "Estimate and Actual live on the same dashboard",
    body: "The toggle in the dashboard header flips every tab between planned numbers and recorded spend. Same categories, same rooms — just a different story.",
  },
  {
    icon: Link2,
    title: "Tag receipts to a line item",
    body: "In Actual mode, hit 'Allocate to a line…' on any entry to attach it to a specific room and work item (or category + variant). Unallocated spend still rolls up to the category.",
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen fc-app-bg text-[#1C1C1C] fc-fade-in" data-testid="how-it-works-page">
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 flex items-center justify-between gap-3 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              data-testid="how-back-btn"
              onClick={() => navigate("/projects")}
              className="w-8 h-8 rounded-lg border border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C] hover:border-black/[0.2] transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="how-logo-btn"
              onClick={() => navigate("/")}
              className="flex items-baseline gap-2.5 sm:gap-3 group shrink-0 min-w-0"
              aria-label="FlipCost by MyPlanCost — home"
            >
              <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">
                FlipCost
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                by MyPlanCost
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="how-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="h-9 px-3 rounded-lg bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 text-sm font-medium flex items-center gap-1.5 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try Demo
            </button>
            <button
              data-testid="how-faq-btn"
              onClick={() => navigate("/faq")}
              className="h-9 px-3 rounded-lg border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-sm text-[#27272A] flex items-center gap-1.5 transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-[#78716C]" /> FAQ
            </button>
            <button
              data-testid="how-get-started-btn"
              onClick={() => navigate("/projects?new=1")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3 text-sm font-medium flex items-center gap-1.5 transition-colors"
            >
              Get started <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-8 sm:py-12 space-y-10 sm:space-y-14">
        {/* Hero */}
        <section data-testid="how-hero">
          <div className="fc-label mb-3">The idea</div>
          <h1 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1C1C1C] max-w-3xl leading-tight">
            Plan your renovation with real numbers — then track what you actually spend.
          </h1>
          <p className="text-base text-[#57534E] mt-5 max-w-2xl leading-relaxed">
            FlipCost is a decision engine, not a quoting tool. Model the work room-by-room to see estimated cost, profit, resale uplift, or total budget — framed around your goal. Then flip to Actual mode and log receipts as the build progresses, tagged to the very rooms and line items you planned.
          </p>

          {/* Mobile-only inline CTA row — Clarity recordings showed mobile
              visitors scroll past the header buttons before reading them.
              Repeating Try Demo / FAQ / Get started immediately after the
              first paragraph (where mobile eyes naturally land) recovers
              that lost touchpoint. Desktop hides this row because the
              same actions are visible in the header at >=md widths. */}
          <div
            className="md:hidden mt-6 flex flex-wrap items-center gap-2"
            data-testid="how-hero-mobile-cta-row"
          >
            <button
              type="button"
              data-testid="how-hero-mobile-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="h-10 px-3.5 rounded-lg bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try Demo
            </button>
            <button
              type="button"
              data-testid="how-hero-mobile-faq-btn"
              onClick={() => navigate("/faq")}
              className="h-10 px-3.5 rounded-lg border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-sm text-[#27272A] inline-flex items-center gap-1.5 transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-[#78716C]" /> FAQ
            </button>
            <button
              type="button"
              data-testid="how-hero-mobile-get-started-btn"
              onClick={() => navigate("/projects?new=1")}
              className="h-10 px-3.5 rounded-lg bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              Get started <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div
            className="mt-7 rounded-2xl bg-sage-50 border border-sage-200 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 max-w-3xl"
            data-testid="how-hero-demo-bar"
          >
            <div>
              <div className="fc-label text-sage-700">Don't read — try it</div>
              <div className="fc-heading text-[15px] text-[#1C1C1C] mt-1.5">
                Open the live demo and see the dashboard in action.
              </div>
              <p className="text-sm text-[#57534E] mt-1 leading-relaxed">
                Pre-loaded Cape Town flip · all 6 tabs editable · no signup, no payment.
              </p>
            </div>
            <button
              type="button"
              data-testid="how-hero-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5 text-[15px] font-medium inline-flex items-center gap-2 shrink-0"
            >
              <Play className="w-4 h-4" /> Try the Live Demo
            </button>
          </div>
        </section>

        {/* Goals */}
        <section data-testid="how-goals">
          <div className="fc-label mb-4">Three ways to use it</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {GOALS.map((g, i) => {
              const Icon = g.icon;
              return (
                <div key={g.title} className="fc-card p-5" data-testid={`how-goal-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 text-sage-700 grid place-items-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="fc-heading text-[#1C1C1C] text-[15px] font-medium mt-4">{g.title}</div>
                  <p className="text-sm text-[#57534E] mt-1.5 leading-relaxed">{g.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Steps */}
        <section data-testid="how-steps">
          <div className="fc-label mb-4">The flow</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  className="fc-card p-6"
                  data-testid={`step-${s.n}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 flex flex-col items-center">
                      <div className="w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="fc-mono text-[10px] text-sage-700 mt-2 tracking-wider">{s.n}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="fc-heading text-[#1C1C1C] text-[15px] font-medium">{s.title}</div>
                      <p className="text-sm text-[#57534E] mt-1.5 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Actual-mode spotlight */}
        <section data-testid="how-actual">
          <div className="fc-card p-8 sm:p-10 bg-slate-brand-50 border border-slate-brand-200">
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-8 items-start">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-brand-700 bg-slate-brand-100 border border-slate-brand-200 rounded-full px-2.5 py-1">
                  <Receipt className="w-3 h-3" /> Actual spend tracker
                </div>
                <h2 className="fc-heading text-2xl text-[#1C1C1C] mt-4 leading-snug">
                  Your plan and your spend, side by side — on the same dashboard.
                </h2>
                <p className="text-sm text-[#57534E] mt-3 leading-relaxed">
                  Flip the <strong>Estimate · Actual</strong> toggle in the dashboard header and every tab switches into tracking mode. Overview shows spent / remaining / projected profit / % of budget. Rooms, Exterior, Systems and Soft Costs each become a focused spend log for that bucket.
                </p>
              </div>
              <div className="space-y-3">
                <FeatureRow
                  icon={PenLine}
                  title="One-tap toggle"
                  body="Sage for Estimate, slate-blue for Actual. The whole dashboard re-themes so you always know which mode you're in."
                />
                <FeatureRow
                  icon={Link2}
                  title="Allocate to a line (optional)"
                  body="Tag a receipt to a specific room + work item, or a category + variant. Unallocated entries still roll up to the category."
                />
                <FeatureRow
                  icon={Layers}
                  title="Per-room drill on the Rooms tab"
                  body="Planned vs actual for every room, plus an Unassigned bucket. Click a row to filter the entries list below."
                />
              </div>
            </div>
          </div>
        </section>


        {/* Tips / How-to */}
        <section data-testid="how-tips">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="fc-label">Tips &amp; how-to</div>
              <h2 className="fc-heading text-2xl text-[#1C1C1C] mt-1">Get more out of it</h2>
            </div>
            <div className="text-xs text-[#A8A29E]">Practical shortcuts from the field</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIPS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={t.title} className="fc-card p-5" data-testid={`tip-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="fc-heading text-[#1C1C1C] text-[14px] font-medium mt-4">{t.title}</div>
                  <p className="text-sm text-[#57534E] mt-1.5 leading-relaxed">{t.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="fc-card p-8 text-center" data-testid="how-cta">
          <h3 className="fc-heading text-xl text-[#1C1C1C]">Ready to plan your next renovation?</h3>
          <p className="text-sm text-[#57534E] mt-2 max-w-md mx-auto">
            Try the live demo first if you'd rather poke around — or jump straight in and create a project. Either way, you'll see a clear picture in under sixty seconds.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <button
              data-testid="how-start-project-btn"
              onClick={() => navigate("/projects?new=1")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              Start a project <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="how-cta-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 hover:text-sage-800 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try the Live Demo
            </button>
            <button
              data-testid="how-cta-faq-btn"
              onClick={() => navigate("/faq")}
              className="rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-[#1C1C1C] transition-colors"
            >
              <HelpCircle className="w-4 h-4 text-[#78716C]" /> Read the FAQ
            </button>
          </div>
        </section>
        <DisclaimerBar />
      </main>
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

function FeatureRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-slate-brand-100 border border-slate-brand-200 text-slate-brand-700 grid place-items-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="fc-heading text-[14px] font-medium text-[#1C1C1C]">{title}</div>
        <p className="text-[13px] text-[#57534E] mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
